import React, { useEffect, useMemo, useRef, useState } from 'react';
import { exportCSV, uid, grade } from '../db';
import {
  toast, Badge, Btn, Card, CardHead, Modal, ModalFooter,
  Field, Input, Select, Grid, Span, SecLabel, Search,
  Avatar, Tabs, PhotoZone, NoData, TblWrap,
} from '../ui.jsx';
import {
  buildTeacherCard, buildTeacherCardBack, printTeacherCard,
} from '../print.js';
import { fmtDob } from '../utils/fmtDob';

const SUBJ_PILLS = [
  'bg-secondary-container/20 text-secondary',
  'bg-tertiary-fixed text-on-tertiary-fixed-variant',
  'bg-primary-container text-on-primary',
  'bg-surface-container text-on-surface-variant',
  'bg-error-container/40 text-error',
];

function FeeExtraRow({ onAdd }) {
  const [label, setLabel] = useState('');
  const [amt, setAmt] = useState('');
  return (
    <div className="flex gap-2 mt-1">
      <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Charge name (e.g. Sports Fee)"
        className="flex-1 px-3 py-2 bg-surface-container-low border-0 rounded-lg text-sm text-on-surface placeholder:text-outline focus:outline-none focus:ring-2 focus:ring-primary/20" />
      <input type="number" value={amt} onChange={e => setAmt(e.target.value)} placeholder="₹"
        className="w-24 px-3 py-2 bg-surface-container-low border-0 rounded-lg text-sm text-on-surface placeholder:text-outline focus:outline-none focus:ring-2 focus:ring-primary/20" />
      <button type="button" onClick={() => {
        if (!label.trim() || !parseFloat(amt)) { toast('Enter name and amount', 'err'); return; }
        onAdd(label.trim(), amt); setLabel(''); setAmt('');
      }} className="px-3 py-2 bg-primary text-on-primary rounded-lg text-xs font-bold border-0 cursor-pointer hover:opacity-90 transition-all">+ Add</button>
    </div>
  );
}

function Teachers({ db, save }) {
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = React.useRef(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [view, setView] = useState('cards'); // 'cards' | 'directory'
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [tphoto, setTphoto] = useState(null);
  const [editId, setEditId] = useState(null);
  const [showPpass, setShowPpass] = useState(false);
  const classes = db.classes.map(c => c.name);
  const sf = k => v => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    const h = e => { if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const suggestions = React.useMemo(() => {
    if (!search.trim()) return [];
    const lq = search.toLowerCase();
    return db.teachers.filter(t =>
      `${t.fn || ''} ${t.ln || ''}`.toLowerCase().includes(lq) ||
      (t.su || '').toLowerCase().includes(lq) ||
      (t.empId || '').toLowerCase().includes(lq) ||
      (t.cls || '').toLowerCase().includes(lq) ||
      (t.ph || '').includes(lq)
    ).slice(0, 8);
  }, [search, db.teachers]);

  const openAdd = () => { setForm({ status: 'Active' }); setTphoto(null); setEditId(null); setOpen(true); };
  const openEdit = t => { setForm({ ...t }); setTphoto((db.tphotos || {})[t.id] || null); setEditId(t.id); setOpen(true); };
  const saveTea = () => {
    if (!form.fn?.trim()) { toast('Name required', 'err'); return; }
    const id = editId || 'T' + uid();
    const t = { ...form, id, ppass: form.ppass || (editId ? db.teachers.find(x => x.id === id)?.ppass : '') };
    const newT = editId ? db.teachers.map(x => x.id === id ? t : x) : [...db.teachers, t];
    const newTP = { ...(db.tphotos || {}) };
    if (tphoto) newTP[id] = tphoto; else if (editId) delete newTP[id];
    save({ ...db, teachers: newT, tphotos: newTP }); setOpen(false); toast(editId ? 'Updated' : 'Teacher added');
  };
  const delTea = id => {
    if (!window.confirm('Delete?')) return;
    const newTP = { ...(db.tphotos || {}) }; delete newTP[id];
    save({ ...db, teachers: db.teachers.filter(t => t.id !== id), tphotos: newTP }); toast('Deleted', 'err');
  };

  const filtered = db.teachers.filter(t => {
    const txt = (t.fn + ' ' + t.ln + ' ' + (t.su || '') + ' ' + (t.empId || '')).toLowerCase();
    return txt.includes(search.toLowerCase()) && (!filterStatus || t.status === filterStatus);
  });

  const statusDot = s => s === 'Active' ? 'bg-emerald-500' : s === 'On Leave' ? 'bg-amber-500' : 'bg-error';
  const statusLabel = s => s === 'Active' ? 'Active' : s === 'On Leave' ? 'On Leave' : s || 'Active';

  // Card background accent colours cycling
  const ACCENTS = ['bg-secondary/5', 'bg-tertiary-fixed/10', 'bg-primary/5', 'bg-secondary/5', 'bg-primary/5', 'bg-tertiary-fixed/10'];

  return (
    <div>
      {/* Hero Header */}
      <div className="directory-hero mb-6 md:mb-8 flex flex-col md:flex-row md:justify-between md:items-end gap-3 md:gap-4">
        <div>
          <h1 className="text-4xl font-extrabold text-primary tracking-tight mb-2 font-headline">Staff</h1>
          <p className="text-on-surface-variant">
            {db.teachers.length} staff member{db.teachers.length !== 1 ? 's' : ''} · {db.settings.year || '2025-26'}
          </p>
        </div>
        <div className="directory-hero-actions flex flex-wrap gap-2 md:gap-3 w-full md:w-auto">
          <button onClick={openAdd}
            className="flex items-center justify-center gap-2 px-4 md:px-6 py-2.5 rounded-xl bg-gradient-to-br from-primary to-primary-container text-white shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all font-semibold text-sm border-0 cursor-pointer font-sans flex-1 md:flex-none">
            <span className="material-symbols-outlined text-sm">add</span> New Staff
          </button>
          <button className="directory-desktop-only flex items-center justify-center gap-2 px-4 md:px-5 py-2.5 rounded-xl border border-outline-variant hover:bg-surface-container transition-all font-semibold text-sm text-primary bg-transparent cursor-pointer font-sans flex-1 md:flex-none">
            <span className="material-symbols-outlined text-sm">export_notes</span> Export List
          </button>
        </div>
      </div>

      {/* View toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[{ id: 'cards', icon: 'grid_view', label: 'Faculty' }, { id: 'directory', icon: 'person_search', label: 'Directory' }].map(v => (
          <button key={v.id} onClick={() => setView(v.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, transition: 'all 150ms',
              background: view === v.id ? '#1960a3' : '#f1f5f9', color: view === v.id ? '#fff' : '#64748b', boxShadow: view === v.id ? '0 2px 8px rgba(25,96,163,0.2)' : 'none'
            }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{v.icon}</span>{v.label}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div className="directory-filter-row" style={{ display: 'flex', gap: 12, marginBottom: 32, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Search with live dropdown */}
        <div className="directory-search-group" ref={searchRef} style={{ position: 'relative' }}>
          <span className="material-symbols-outlined" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: '#94a3b8', pointerEvents: 'none', zIndex: 1 }}>search</span>
          <input
            style={{ paddingLeft: 36, paddingRight: search ? 32 : 16, paddingTop: 9, paddingBottom: 9, borderRadius: 24, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 13, color: '#1e293b', outline: 'none', width: 'min(100%, 280px)', transition: 'all 200ms', fontFamily: 'inherit' }}
            value={search}
            onChange={e => { setSearch(e.target.value); setSearchOpen(true); }}
            onFocus={e => { e.target.style.borderColor = '#1960a3'; e.target.style.background = '#f0f6ff'; setSearchOpen(true); }}
            onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = '#f8fafc'; }}
            onKeyDown={e => { if (e.key === 'Escape') { setSearch(''); setSearchOpen(false); } }}
            placeholder="Search by name, subject, class…"
          />
          {search && (
            <button onClick={() => { setSearch(''); setSearchOpen(false); }} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', color: '#94a3b8', zIndex: 1 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>close</span>
            </button>
          )}
          {/* Live dropdown */}
          {searchOpen && search && (
            <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, width: 'min(92vw, 340px)', background: '#fff', borderRadius: 14, boxShadow: '0 8px 32px rgba(0,32,69,0.14)', border: '1px solid #e8edf5', zIndex: 999, overflow: 'hidden' }}>
              {suggestions.length > 0 ? suggestions.map((t, i) => {
                const photo = (db.tphotos || {})[t.id];
                const ini = `${(t.fn || '')[0] || ''}${(t.ln || '')[0] || ''}`.toUpperCase();
                const statusColor = t.status === 'Active' ? '#059669' : t.status === 'On Leave' ? '#d97706' : '#ef4444';
                const statusBg = t.status === 'Active' ? '#d1fae5' : t.status === 'On Leave' ? '#fef3c7' : '#fee2e2';
                return (
                  <button key={t.id} onMouseDown={() => { setSearch(`${t.fn} ${t.ln}`); setSearchOpen(false); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', transition: 'background 120ms', borderBottom: '1px solid #f8fafc' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f0f6ff'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    {/* avatar */}
                    <div style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0, overflow: 'hidden', background: 'linear-gradient(135deg,#059669,#0891b2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: '#fff' }}>
                      {photo ? <img src={photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : ini}
                    </div>
                    {/* info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.fn} {t.ln}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{t.su || '—'}{t.cls ? ` · ${t.cls}` : ''}{t.empId ? ` · ${t.empId}` : ''}</div>
                    </div>
                    {/* status badge */}
                    <span style={{ fontSize: 10, fontWeight: 700, flexShrink: 0, padding: '3px 8px', borderRadius: 20, background: statusBg, color: statusColor }}>
                      {t.status || 'Active'}
                    </span>
                  </button>
                );
              }) : (
                <div style={{ padding: '20px 16px', textAlign: 'center' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg,#ecfdf5,#d1fae5)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 22, color: '#059669', fontVariationSettings: "'FILL' 0,'wght' 300" }}>manage_search</span>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', marginBottom: 2 }}>No faculty match</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>"{search}"</div>
                </div>
              )}
            </div>
          )}
        </div>
        {/* Status filter pills */}
        {['', 'Active', 'On Leave', 'Resigned'].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className="directory-desktop-only"
            style={{
              padding: '8px 18px', borderRadius: 24, border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 150ms',
              background: filterStatus === s ? '#1960a3' : '#f1f5f9',
              color: filterStatus === s ? '#fff' : '#64748b',
              boxShadow: filterStatus === s ? '0 2px 8px rgba(25,96,163,0.25)' : 'none',
            }}>
            {s || 'All Staff'}
          </button>
        ))}
        {/* result count */}
        {(search || filterStatus) && (
          <span style={{ fontSize: 12, color: '#64748b', marginLeft: 4 }}>
            {filtered.length} result{filtered.length !== 1 ? 's' : ''}
            <button onClick={() => { setSearch(''); setFilterStatus(''); }} style={{ marginLeft: 8, fontSize: 11, color: '#ef4444', background: '#fee2e2', border: 'none', borderRadius: 8, padding: '3px 8px', cursor: 'pointer', fontWeight: 600 }}>Clear</button>
          </span>
        )}
      </div>

      {/* Cards Grid (Faculty view) */}
      {view === 'cards' && (filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {filtered.map((t, i) => {
            const name = t.fn + ' ' + t.ln;
            const ini = name.trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
            const pillCls = SUBJ_PILLS[i % SUBJ_PILLS.length];
            const accentCls = ACCENTS[i % ACCENTS.length];
            const classTaught = db.students.filter(s => s.cls === t.cls).length;
            return (
              <div key={t.id} className="group bg-surface-container-lowest p-7 rounded-3xl shadow-sm hover:shadow-xl transition-all relative overflow-hidden">
                {/* Decorative circle */}
                <div className={`absolute top-0 right-0 w-32 h-32 ${accentCls} rounded-full -mr-16 -mt-16 transition-transform group-hover:scale-110`}></div>

                {/* Top row: avatar + name + subject pill */}
                <div className="flex items-start justify-between relative mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-surface-container-low flex-shrink-0 overflow-hidden shadow-sm">
                      {(db.tphotos || {})[t.id]
                        ? <img src={(db.tphotos || {})[t.id]} alt={t.fn} className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center text-lg font-bold text-primary bg-primary/8">{ini}</div>
                      }
                    </div>
                    <div>
                      <h3 className="text-lg font-bold font-headline text-primary leading-tight">{name}</h3>
                      <p className="text-xs text-on-surface-variant uppercase tracking-wide mt-0.5">
                        {t.qual || (t.cls ? 'Class Teacher · ' + t.cls : 'Faculty')}
                      </p>
                    </div>
                  </div>
                  {t.su && (
                    <span className={`px-3 py-1 text-[10px] font-bold rounded-full uppercase tracking-widest flex-shrink-0 ${pillCls}`}>
                      {t.su}
                    </span>
                  )}
                </div>

                {/* Contact info */}
                <div className="space-y-2.5 mb-5">
                  {t.em && (
                    <div className="flex items-center gap-2.5 text-sm text-on-surface">
                      <span className="material-symbols-outlined text-primary text-[16px]">alternate_email</span>
                      <span className="truncate">{t.em}</span>
                    </div>
                  )}
                  {(t.ph || t.empId) && (
                    <div className="flex items-center gap-2.5 text-sm text-on-surface">
                      <span className="material-symbols-outlined text-primary text-[16px]">{t.ph ? 'call' : 'badge'}</span>
                      <span>{t.ph || 'ID: ' + t.empId}</span>
                    </div>
                  )}
                  {t.sal > 0 && (
                    <div className="flex items-center gap-2.5 text-sm text-on-surface">
                      <span className="material-symbols-outlined text-primary text-[16px]">payments</span>
                      <span>₹{Number(t.sal).toLocaleString('en-IN')}/mo</span>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="pt-4 border-t border-surface-container-high flex items-center justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-tighter text-outline font-bold mb-1">Status</p>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${statusDot(t.status)}`}></span>
                      <span className="text-xs font-bold text-primary">{statusLabel(t.status)}{t.cls ? ' · ' + t.cls : ''}</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(t)} title="Edit"
                      className="w-9 h-9 rounded-full bg-surface-container flex items-center justify-center hover:bg-primary hover:text-white transition-all text-on-surface-variant border-0 cursor-pointer">
                      <span className="material-symbols-outlined text-[16px]">edit</span>
                    </button>
                    <button onClick={() => delTea(t.id)} title="Delete"
                      className="w-9 h-9 rounded-full bg-surface-container flex items-center justify-center hover:bg-error-container hover:text-error transition-all text-on-surface-variant border-0 cursor-pointer">
                      <span className="material-symbols-outlined text-[16px]">delete</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 24, padding: '56px 24px', textAlign: 'center', marginBottom: 40, boxShadow: '0 1px 8px rgba(0,31,77,0.06)' }}>
          <div style={{ width: 88, height: 88, borderRadius: 24, background: 'linear-gradient(135deg,#ecfdf5 0%,#d1fae5 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: '0 4px 20px rgba(5,150,105,0.15)' }}>
            <span className="material-symbols-outlined" style={{ fontSize: 42, color: '#059669', fontVariationSettings: "'FILL' 0,'wght' 300" }}>groups</span>
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#1e293b', marginBottom: 6, fontFamily: 'Manrope,sans-serif' }}>No staff found</div>
          <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>
            {search || filterStatus ? 'Try adjusting your search or filters' : 'Add your first staff member to get started'}
          </div>
          <button onClick={openAdd} style={{ padding: '10px 24px', background: 'linear-gradient(135deg,#059669,#0891b2)', color: '#fff', border: 'none', borderRadius: 12, fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(5,150,105,0.3)', fontFamily: 'inherit' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>person_add</span>
              Add First Staff Member
            </span>
          </button>
        </div>
      ))}

      {/* Directory table (Directory view) */}
      {view === 'directory' && (
        <div>
          <h2 className="text-2xl font-extrabold font-headline text-primary tracking-tight mb-5">Staff Directory</h2>
          <div className="bg-surface-container-low rounded-3xl p-1 overflow-hidden">
            <TblWrap>
              <table>
                <thead>
                  <tr>
                    <th className="px-6 py-5">Staff Name</th>
                    <th className="px-6 py-5">Emp ID</th>
                    <th className="px-6 py-5">Subject</th>
                    <th className="px-6 py-5">Class</th>
                    <th className="px-6 py-5">Status</th>
                    <th className="px-6 py-5">Portal</th>
                    <th className="px-6 py-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t, i) => (
                    <tr key={t.id} className="hover:bg-surface-container-high transition-colors group">
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl flex-shrink-0 overflow-hidden">
                            {(db.tphotos || {})[t.id]
                              ? <img src={(db.tphotos || {})[t.id]} alt={t.fn} className="w-full h-full object-cover" />
                              : <Avatar name={t.fn + ' ' + t.ln} idx={i + 3} />
                            }
                          </div>
                          <div>
                            <div className="font-bold text-primary">{t.fn} {t.ln}</div>
                            <div className="text-[10px] text-outline">{t.em || ''}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5 text-on-surface-variant">{t.empId || '—'}</td>
                      <td className="px-6 py-5 text-on-surface-variant">{t.su || '—'}</td>
                      <td className="px-6 py-5 text-on-surface-variant">{t.cls || '—'}</td>
                      <td className="px-6 py-5">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest ${t.status === 'Active' ? 'bg-secondary-container/30 text-secondary' :
                            t.status === 'On Leave' ? 'bg-tertiary-fixed text-on-tertiary-fixed-variant' :
                              'bg-error-container text-error'}`}>
                          {t.status || 'Active'}
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        {t.puser ? <Badge color="blue">{t.puser}</Badge> : <span className="text-outline text-xs">—</span>}
                      </td>
                      <td className="px-6 py-5 text-right">
                        <button onClick={() => openEdit(t)}
                          className="p-2 rounded-lg text-outline hover:text-primary transition-all border-0 bg-transparent cursor-pointer">
                          <span className="material-symbols-outlined text-[18px]">more_vert</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TblWrap>
          </div>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={(editId ? 'Edit' : 'Add') + ' Staff Member'}>
        <div className="mb-5">
          <div className="text-[10px] font-black text-primary uppercase tracking-widest mb-3 pb-2" style={{ borderBottom: '1px solid rgba(0,32,69,0.1)' }}>Profile Photo</div>
          <PhotoZone photo={tphoto} onUpload={setTphoto} onClear={() => setTphoto(null)} />
        </div>
        <Grid><SecLabel>Details</SecLabel>
          <Field label="First Name *"><Input value={form.fn || ''} onChange={sf('fn')} placeholder="Enter first name" /></Field>
          <Field label="Last Name"><Input value={form.ln || ''} onChange={sf('ln')} placeholder="Enter last name (optional)" /></Field>
          <Field label="Emp ID"><Input value={form.empId || ''} onChange={sf('empId')} placeholder="Enter employee ID" /></Field>
          <Field label="Designation"><Input value={form.desig || ''} onChange={sf('desig')} placeholder="e.g. PGT, TGT, PRT, Principal" /></Field>
          <Field label="Staff Type"><Select value={form.staffType || 'Faculty'} onChange={sf('staffType')}><option value="Faculty">Faculty</option><option value="Peon">Peon</option><option value="Admin Staff">Admin Staff</option><option value="Other">Other</option></Select></Field>
          <Field label="Subject"><Input value={form.su || ''} onChange={sf('su')} placeholder="Subject taught" /></Field>
          <Field label="Class Teacher (can select multiple)">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 0' }}>
              {classes.map(c => {
                const selected = (form.cls || '').split(',').map(x => x.trim()).filter(Boolean).includes(c);
                return (
                  <button key={c} type="button" onClick={() => {
                    const cur = (form.cls || '').split(',').map(x => x.trim()).filter(Boolean);
                    const next = selected ? cur.filter(x => x !== c) : [...cur, c];
                    sf('cls')(next.join(', '));
                  }} style={{ padding: '5px 12px', borderRadius: 20, border: `1.5px solid ${selected ? '#1960a3' : '#e2e8f0'}`, background: selected ? '#1960a3' : '#fff', color: selected ? '#fff' : '#64748b', fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 150ms' }}>
                    {c}
                  </button>
                );
              })}
            </div>
            {form.cls && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Selected: {form.cls}</div>}
          </Field>
          <Field label="Qualification"><Input value={form.qual || ''} onChange={sf('qual')} placeholder="Highest qualification" /></Field>
          <Field label="Phone"><Input value={form.ph || ''} onChange={sf('ph')} placeholder="10-digit mobile number" /></Field>
          <Field label="Email"><Input type="email" value={form.em || ''} onChange={sf('em')} placeholder="Teacher email address" /></Field>
          <Field label="Salary ₹/mo"><Input type="number" value={form.sal || ''} onChange={sf('sal')} placeholder="Monthly salary amount" /></Field>
          <Field label="Joining Date"><Input type="date" value={form.doj || ''} onChange={sf('doj')} /></Field>
          <Field label="Status"><Select value={form.status || 'Active'} onChange={sf('status')}><option>Active</option><option>On Leave</option><option>Resigned</option></Select></Field>
          <Field label="Blood Group"><Select value={form.blood || ''} onChange={sf('blood')}><option value="">—</option>{['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(b => <option key={b}>{b}</option>)}</Select></Field>
          <SecLabel>Portal Access</SecLabel>
          <input type="text" name="prevent_autofill_tea_user" style={{ display: 'none' }} readOnly autoComplete="off" />
          <input type="password" name="prevent_autofill_tea_pass" style={{ display: 'none' }} readOnly autoComplete="off" />
          <Field label="Username"><Input value={form.puser || ''} onChange={sf('puser')} placeholder="Leave blank for no access" autoComplete="off" /></Field>
          <Field label="Password">
            <div className="relative">
              <Input type={showPpass ? "text" : "password"} value={form.ppass || ''} onChange={sf('ppass')} placeholder="Minimum 6 characters" autoComplete="new-password" style={{ paddingRight: 40 }} />
              <button type="button" onClick={() => setShowPpass(!showPpass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-primary transition-colors border-0 bg-transparent cursor-pointer flex items-center justify-center p-1">
                <span className="material-symbols-outlined text-xl">{showPpass ? 'visibility_off' : 'visibility'}</span>
              </button>
            </div>
            <div className="text-xs text-on-surface-variant mt-1 ml-1 opacity-80">
              Leave empty to keep current password
            </div>
          </Field>
        </Grid>
        <ModalFooter><Btn onClick={() => setOpen(false)}>Cancel</Btn><Btn variant="primary" onClick={saveTea}>Save</Btn></ModalFooter>
      </Modal>
    </div>
  );
}


export default Teachers;
